/**
 * @fileoverview Session engagement tier scoring (pure functions).
 * @description Maps action counts, active time, and intent depth into a
 * low-cardinality `session_engagement_tier` for GA4 segmentation.
 *
 * @module lib/site-intelligence/engagement-score
 */

import type { VisitorIntent } from "./intent"

/** Coarse engagement quality band for the tab session. */
export type SessionEngagementTier = "passive" | "browsing" | "engaged" | "high_intent"

/** Inputs for tier classification. */
export type EngagementScoreInput = Readonly<{
  pageCount: number
  actionCount: number
  signalWeight: number
  highIntentActionCount: number
  activeTimeMs: number
  maxIntent: VisitorIntent
  maxScrollDepthPct?: number
}>

/**
 * Classifies session engagement into a single tier label.
 *
 * @param {EngagementScoreInput} input - Session engagement counters.
 * @returns {SessionEngagementTier} Tier for analytics segmentation.
 */
export function classifyEngagementTier(input: EngagementScoreInput): SessionEngagementTier {
  if (
    input.highIntentActionCount > 0 ||
    input.maxIntent === "conversion" ||
    (input.maxIntent === "evaluation" && input.actionCount >= 2)
  ) {
    return "high_intent"
  }

  if (
    input.actionCount >= 3 ||
    input.signalWeight >= 8 ||
    input.activeTimeMs >= 60_000 ||
    (input.maxScrollDepthPct !== undefined && input.maxScrollDepthPct >= 75) ||
    input.pageCount >= 3
  ) {
    return "engaged"
  }

  if (
    input.actionCount >= 1 ||
    input.signalWeight >= 3 ||
    input.pageCount >= 2 ||
    input.activeTimeMs >= 15_000
  ) {
    return "browsing"
  }

  return "passive"
}
